import { IFigureDto } from '../models/IFigureDto';
import { IFigureCollection } from '../models/IFigureCollection';
import { IPaginatedResponse } from '../models/IPaginatedResponse';
import axiosInstance from '../config/axiosInstance';
import { VersionHistory } from '../components/FigureDetail';
import { getLoadedConfig } from '../config/env';
// inside async function
//const { apiUrl, authorityUrl, homeUrl, version, env, clientId, scope, favoritterId, favoritterLabel, favoritterCollectionId, defaultCollectionId } = getLoadedConfig();

// (reserved) keep for future normalization needs

// Extract only the comment text(s) from a JSON-encoded customerComment string.
// Supports shapes: string, { comments: [{ comment, ... }] }, [{ comment, ... }], or single { comment, ... }.
export function extractCommentTextsFromJson(raw: string | undefined | null): string {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  try {
    const parsed = JSON.parse(trimmed);
    let comments: unknown[] = [];
    if (Array.isArray(parsed)) {
      comments = parsed;
    } else if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      if (Array.isArray(obj.comments)) {
        comments = obj.comments as unknown[];
      } else if ('comment' in obj) {
        comments = [obj];
      }
    }
    if (Array.isArray(comments) && comments.length > 0) {
      const texts = comments
        .map((c) => {
          if (typeof c === 'string') return c;
          if (c && typeof c === 'object' && 'comment' in (c as Record<string, unknown>)) {
            const obj = c as Record<string, unknown>;
            const comment = obj.comment;
            const email = typeof obj.email === 'string' ? obj.email : '';
            const datetime = typeof obj.datetime === 'string' ? obj.datetime : '';
            let prefix = '';
            if (email || datetime) {
              let timeStr = '';
              if (datetime) {
                const d = new Date(datetime);
                if (!isNaN(d.valueOf())) {
                  try {
                    timeStr = new Intl.DateTimeFormat(undefined, {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                      timeZone: 'Europe/Oslo',
                    }).format(d);
                  } catch {
                    timeStr = d.toISOString();
                  }
                } else {
                  timeStr = datetime;
                }
              }
              const parts: string[] = [];
              if (email) parts.push(email);
              if (timeStr) parts.push(timeStr);
              prefix = parts.length ? `(${parts.join(' - ')})` : '';
            }
            return typeof comment === 'string' ? (prefix ? `${prefix}\n${comment}` : comment) : '';
          }
          return '';
        })
        .filter((t) => t && t.trim().length > 0) as string[];
      if (texts.length > 0) return texts.join('\n\n');
    }
    // Fallback if structure isn't recognized: return original string
    return raw;
  } catch {
    // Not JSON: return as-is
    return raw;
  }
}

export default class FigurService {
  private static readonly isDevOrTest = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test';
  private static readonly apiPrefix = this.isDevOrTest ? '/api' : ''; // Use '/api' only for dev/test proxy
  private static readonly baseUrl = `${this.apiPrefix}/figure`;

  /**
   * Retrieve all figures for a given collection with pagination.
   */
  static async getFigures(collectionId: number, page: number = 1, limit: number = 10, series: number = 2): Promise<IPaginatedResponse<IFigureDto>> {
    try {
      const { data } = await axiosInstance.get<IPaginatedResponse<IFigureDto>>(`${this.baseUrl}/getall`, {
        params: { collectionId, page, limit, series },
        headers: { Accept: 'application/json' },
      });
      return data;
    } catch (error) {
      console.error('Error fetching figures:', error);
      throw error;
    }
  }

  /**
   * Retrieve favorite figures with pagination.
   * and then returns the processed data.
   */
  static async getFavoriteFigures(page: number = 1, limit: number = 10, series: number = 0): Promise<IPaginatedResponse<IFigureDto>> {
    try {
      const { data } = await axiosInstance.get<IPaginatedResponse<IFigureDto>>(`${this.baseUrl}/favorites`, {
        params: { page, limit, series },
      });
      const ids = data.data.map((f) => f.imageId);
      const uniqueCount = new Set(ids).size;
      if (uniqueCount !== ids.length) {
        console.warn('Favorites API returned duplicate imageIds', { page, limit, total: ids.length, unique: uniqueCount });
      } else {
        console.debug('Favorites API page fetched', { page, count: ids.length });
      }
      // Deduplicate by imageId to guard against transient API duplicates
      const seen = new Set<number>();
      const deduped = data.data.filter((f) => {
        if (seen.has(f.imageId)) return false;
        seen.add(f.imageId);
        return true;
      });
      // deduping, BUT this should not be needed, as the API should not return duplicates! TODO
      const processedData = {
        data: deduped.map((figure) => ({
          ...figure,
          customerFavorite: true,
          customerMetadata: figure.customerMetadata ?? '',
        })),
        nextCursor: data.nextCursor,
        hasMore: data.hasMore,
        totalCount: data.totalCount - (data.data.length - deduped.length),
      };
      return processedData;
    } catch (error) {
      console.error('Error fetching favorite figures:', error);
      throw error;
    }
  }

  /**
   * Retrieve a figure by its ID and collection.
   */
  static async getFigureByIdAndCollection(id: number, collectionId: number): Promise<IFigureDto> {
    try {
      const { data } = await axiosInstance.get<IFigureDto>(`${this.baseUrl}/getFigureByIdAndCollection`, {
        params: { id, collectionId },
      });
      return data;
    } catch (error) {
      console.error('Error fetching figure:', error);
      throw error;
    }
  }

  /**
   * Retrieve collections for a customer.
   */
  static async getCollectionsByCustomer(customerId: number): Promise<IFigureCollection[]> {
    try {
      const { data } = await axiosInstance.get<IFigureCollection[]>(`${this.baseUrl}/collectionsbycustomerid`, {
        params: { customerId },
      });
      return data;
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }
  }

  /**
   * Get all unique tags from CustomerMetadata and Metadata for a customer.
   */
  static async getAllTags(customerId: number = 1): Promise<string[]> {
    try {
      const { data } = await axiosInstance.get<string[]>(`${this.baseUrl}/alltags`, {
        params: { customerId },
      });
      return data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  static readonly addFigureToCollection = async (collectionId: number, imageId: number): Promise<boolean> => {
    try {
      const response = await axiosInstance.post<boolean>(`${this.baseUrl}/addfiguretocollection`, null, {
        params: { collectionId, imageId },
      });
      return response.data;
    } catch (error) {
      console.error('Error adding figure to collection:', error);
      throw error;
    }
  };

  static readonly removeFigureFromCollection = async (collectionId: number, imageId: number): Promise<boolean> => {
    try {
      const response = await axiosInstance.post<boolean>(`${this.baseUrl}/removefigurefromcollection`, null, {
        params: { collectionId, imageId },
      });
      return response.data;
    } catch (error) {
      console.error('Error removing figure from collection:', error);
      throw error;
    }
  };

  static readonly addCollection = async (name: string, customerId: number, comment: string, color?: string): Promise<IFigureCollection> => {
    // is this promise correct?
    try {
      const { data } = await axiosInstance.post<IFigureCollection>(`${this.baseUrl}/addcollection`, null, {
        params: { name, customerId, comment, color },
      });
      return data;
    } catch (error) {
      console.error('Error adding collection:', error);
      throw error;
    }
  };

  static readonly updateCollectionDetails = async (
    collectionId: number,
    name: string,
    customerId: number,
    comment: string,
    headerComment: string,
    color?: string,
  ): Promise<IFigureCollection> => {
    try {
      const { data } = await axiosInstance.post<IFigureCollection>(`${this.baseUrl}/updatecollection`, null, {
        params: { collectionId, name, customerId, comment, headerComment, color },
      });
      return data;
    } catch (error) {
      console.error('Error updating collection:', error);
      throw error;
    }
  };

  static readonly removeCollection = async (collectionId: number, customerId: number): Promise<void> => {
    try {
      await axiosInstance.delete(`${this.baseUrl}/removecollection`, {
        params: { collectionId, customerId },
      });
    } catch (error) {
      console.error('Error removing collection:', error);
      throw error;
    }
  };

  static readonly getVersionHistory = async (docId: number): Promise<VersionHistory[]> => {
    try {
      const { data } = await axiosInstance.get<VersionHistory[]>(`${this.baseUrl}/versionhistory`, {
        params: { docId },
      });
      return data;
    } catch (error) {
      console.error('Error fetching version history:', error);
      throw error;
    }
  };

  /**
   * Save or update a figure customer data.
   * It is the only data that gets savedd for a specific figure apart from collections.
   */
  static async saveFigure(figure: IFigureDto, collectionId: number): Promise<IFigureDto> {
    try {
      console.log(`Saving figure ${figure.imageId} in collection ${collectionId}, customerFavorite=${figure.customerFavorite}`);

      // Make a copy of the figure to ensure we don't modify the original
      const figureCopy = { ...figure };

      // Ensure customerFavorite is explicitly set as a boolean (not undefined)
      figureCopy.customerFavorite = Boolean(figureCopy.customerFavorite);

      console.log(`Sending figure with customerFavorite=${figureCopy.customerFavorite} to server`);

      await axiosInstance.post<IFigureDto>(`${this.baseUrl}/update`, figureCopy, {
        params: { collectionId },
      });

      // The backend returns an empty string on success.
      // We will return the figure object we sent to the server, as it reflects the new state.
      return figureCopy;
    } catch (error) {
      console.error('Error saving figure:', error);
      // Log more details about the request for debugging
      console.error('Failed request details:', {
        figureId: figure.imageId,
        collectionId,
        customerFavorite: figure.customerFavorite,
      });
      throw error;
    }
  }

  static readonly getAppSettings = async () => {
    try {
      const { data } = await axiosInstance.get<{ urlPrefix: string }>('/api/settings');
      return data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  };
  static readonly getCollections = async () => {
    try {
      const { data } = await axiosInstance.get<IFigureCollection[]>(`${this.baseUrl}/collection`);
      return data;
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }
  };

  static readonly updateCollection = async (comment: string) => {
    try {
      await axiosInstance.post(
        `${this.baseUrl}/collection`,
        { comment: comment.trim() },
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
    } catch (error) {
      console.error('Error updating collection:', error);
      throw error;
    }
  };

  static readonly searchCustomerData = async (
    searchTerm: string,
    collectionId: number = Number(getLoadedConfig()?.favoritterCollectionId ?? 0),
    page: number = 1,
    limit: number = 100,
  ): Promise<IPaginatedResponse<IFigureDto>> => {
    try {
      const response = await axiosInstance.get<IPaginatedResponse<IFigureDto>>(`${this.baseUrl}/customersearch`, {
        params: {
          searchTerm,
          collectionId,
          page,
          limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching customer data:', error);
      throw error;
    }
  };

  static async aiSearchImages(
    searchTerm: string,
    collectionId = Number(getLoadedConfig()?.favoritterCollectionId ?? 0),
    page = 1,
    limit = 100,
  ): Promise<IPaginatedResponse<IFigureDto>> {
    try {
      const { data: figures } = await axiosInstance.get<IFigureDto[]>(`${this.apiPrefix}/Retrieval/images`, {
        params: { query: searchTerm, collectionId, page, limit },
      });

      const mappedData = figures.map((figure) => ({
        ...figure,
        isAiSearchResult: true,
      }));

      return {
        data: mappedData,
        nextCursor: null,
        hasMore: false,
        totalCount: mappedData.length,
      };
    } catch (error) {
      console.error('Error AI searching customer data:', error);
      throw error;
    }
  }

  /**
   * Tags-only, cross-collection search using CustomerMetadata.
   */
  static async searchByTags(
    tags: string[],
    page: number = 1,
    limit: number = 100,
    customerId: number = 1,
    match: 'any' | 'all' = 'any',
  ): Promise<IPaginatedResponse<IFigureDto>> {
    try {
      const { data } = await axiosInstance.get<IPaginatedResponse<IFigureDto>>(`${this.baseUrl}/bytags`, {
        params: {
          tags: tags.join(','),
          page,
          limit,
          customerId,
          match,
        },
      });
      return data;
    } catch (error) {
      console.error('Error searching by tags:', error);
      throw error;
    }
  }
}
export async function getFigurerAsync(collectionType: number): Promise<IFigureDto[]> {
  const response = await FigurService.getFigures(collectionType);
  return response.data;
}
